import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Sparkles,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { createTenant } from "@/api/endpoints/tenants";
import PageHelp from "@/components/Helper/PageHelp";
import { openHelper } from "@/components/Helper/HelperWidget";
import { allocateAddress, getPrefixes, type Prefix } from "@/api/endpoints/ipam";
import type { Tenant } from "@/types";

// Onboarding wizard — single-shot flow that:
//  1. creates a tenant (backend auto-provisions matching NetBox tenant);
//  2. lets the operator pick a prefix and allocate the first IP;
//  3. shows a summary so the new tenant immediately has visible data.

interface SlugFieldProps {
  value: string;
  onChange: (v: string) => void;
}

function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
}

function SlugField({ value, onChange }: SlugFieldProps) {
  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="auto-derive depuis le nom"
      pattern="[a-z0-9-]+"
      className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
    />
  );
}

export default function Onboarding() {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [createdTenant, setCreatedTenant] = useState<Tenant | null>(null);
  const [selectedPrefix, setSelectedPrefix] = useState<number | null>(null);
  const [allocatedIp, setAllocatedIp] = useState<string | null>(null);

  const prefixesQuery = useQuery({
    queryKey: ["ipam", "prefixes", createdTenant?.id],
    queryFn: () => getPrefixes(createdTenant?.id),
    enabled: step === 2 && !!createdTenant,
  });

  const tenantMut = useMutation({
    mutationFn: () =>
      createTenant({
        name: name.trim(),
        slug: slug.trim() || deriveSlug(name),
        role: "client",
      }),
    onSuccess: (t) => {
      setCreatedTenant(t);
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      setStep(2);
    },
  });

  const ipMut = useMutation({
    mutationFn: () => {
      if (!selectedPrefix || !createdTenant) {
        throw new Error("Prefix or tenant missing");
      }
      return allocateAddress({
        prefix_id: selectedPrefix,
        tenant_id: createdTenant.id,
        description: `Onboarding allocation for ${createdTenant.name}`,
      });
    },
    onSuccess: (data) => {
      setAllocatedIp(
        (data as { address?: string }).address ?? "(unknown)",
      );
      queryClient.invalidateQueries({ queryKey: ["ipam-addresses"] });
      setStep(3);
    },
  });

  const reset = () => {
    setStep(1);
    setName("");
    setSlug("");
    setCreatedTenant(null);
    setSelectedPrefix(null);
    setAllocatedIp(null);
    tenantMut.reset();
    ipMut.reset();
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-amber-400" />
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Onboarding tenant</h1>
            <p className="text-sm text-gray-400">
              Cree un tenant + provisionne NetBox + alloue la premiere IP, le
              tout en une operation.
            </p>
          </div>
        </div>
        <PageHelp onAskHelper={openHelper} />
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h2 className="mb-4 text-sm font-semibold text-gray-200">
            Etape 1 — Tenant
          </h2>
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
                Nom
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (!slug) setSlug(deriveSlug(e.target.value));
                }}
                placeholder="ex: Acme Corp"
                className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
                Slug{" "}
                <span className="text-gray-600">
                  (lowercase, chiffres, tirets)
                </span>
              </label>
              <SlugField value={slug || deriveSlug(name)} onChange={setSlug} />
            </div>
            {tenantMut.isError && (
              <p className="flex items-center gap-2 text-sm text-red-400">
                <AlertTriangle className="h-4 w-4" />{" "}
                {(tenantMut.error as Error).message}
              </p>
            )}
            <button
              onClick={() => tenantMut.mutate()}
              disabled={!name.trim() || tenantMut.isPending}
              className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              {tenantMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Creer le tenant
            </button>
          </div>
        </section>
      )}

      {step === 2 && createdTenant && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h2 className="mb-4 text-sm font-semibold text-gray-200">
            Etape 2 — Premiere IP
          </h2>
          <div className="mb-4 rounded border border-emerald-700/40 bg-emerald-900/20 px-3 py-2 text-sm text-emerald-200">
            <CheckCircle2 className="mr-2 inline h-4 w-4" />
            Tenant <strong>{createdTenant.name}</strong> cree (id #
            {createdTenant.id}, NetBox #
            {createdTenant.netbox_tenant_id ?? "?"}, Keycloak{" "}
            {createdTenant.keycloak_group_id ?? "?"})
          </div>
          {prefixesQuery.isLoading && (
            <p className="text-sm text-gray-500">Chargement des prefixes...</p>
          )}
          {prefixesQuery.error && (
            <p className="flex items-center gap-2 text-sm text-red-400">
              <AlertTriangle className="h-4 w-4" /> Impossible de lister les
              prefixes.
            </p>
          )}
          {prefixesQuery.data && prefixesQuery.data.length === 0 && (
            <div className="rounded border border-amber-700/40 bg-amber-900/20 px-3 py-3 text-sm text-amber-200">
              <p>
                Aucun prefix dispo pour ce tenant. Cree d'abord un prefix dans
                NetBox puis reviens.
              </p>
              <button
                onClick={() => setStep(3)}
                className="mt-2 rounded border border-amber-600 px-3 py-1 text-xs hover:bg-amber-700/30"
              >
                Passer cette etape
              </button>
            </div>
          )}
          {prefixesQuery.data && prefixesQuery.data.length > 0 && (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs uppercase tracking-wide text-gray-500">
                  Prefix source
                </label>
                <select
                  value={selectedPrefix ?? ""}
                  onChange={(e) =>
                    setSelectedPrefix(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  className="w-full rounded border border-gray-700 bg-gray-800 px-3 py-2 font-mono text-sm text-gray-100 focus:border-blue-500 focus:outline-none"
                >
                  <option value="">— Choisir un prefix —</option>
                  {prefixesQuery.data.map((p: Prefix) => (
                    <option key={p.id} value={p.id}>
                      {p.prefix} {p.description ? `— ${p.description}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {ipMut.isError && (
                <p className="flex items-center gap-2 text-sm text-red-400">
                  <AlertTriangle className="h-4 w-4" />{" "}
                  {(ipMut.error as Error).message}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => ipMut.mutate()}
                  disabled={!selectedPrefix || ipMut.isPending}
                  className="flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
                >
                  {ipMut.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowRight className="h-4 w-4" />
                  )}
                  Allouer la premiere IP
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
                >
                  Passer
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {step === 3 && createdTenant && (
        <section className="rounded-lg border border-gray-800 bg-gray-900 p-6">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-200">
            <CheckCircle2 className="h-5 w-5 text-emerald-500" /> Etape 3 —
            Termine
          </h2>
          <dl className="space-y-2 text-sm">
            <Row label="Tenant" value={`${createdTenant.name} (#${createdTenant.id})`} />
            <Row label="Slug" value={createdTenant.slug} />
            <Row
              label="NetBox tenant id"
              value={createdTenant.netbox_tenant_id ?? "(non mappe)"}
            />
            <Row
              label="Keycloak group id"
              value={createdTenant.keycloak_group_id ?? "(non mappe)"}
            />
            <Row label="Premiere IP" value={allocatedIp ?? "(non allouee)"} />
          </dl>
          <div className="mt-6 flex gap-2">
            <button
              onClick={reset}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500"
            >
              Onboarder un autre tenant
            </button>
            <a
              href="/ipam"
              className="rounded border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800"
            >
              Voir IPAM
            </a>
          </div>
        </section>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex gap-2">
      <dt className="min-w-[140px] text-xs uppercase tracking-wide text-gray-500">
        {label}
      </dt>
      <dd className="font-mono text-xs text-gray-200">{value}</dd>
    </div>
  );
}

function Stepper({ step }: { step: 1 | 2 | 3 }) {
  const labels = ["Tenant", "Premiere IP", "Termine"];
  return (
    <ol className="flex items-center gap-2 text-xs">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = n === step;
        const done = n < step;
        return (
          <li key={n} className="flex items-center gap-2">
            <span
              className={
                "flex h-6 w-6 items-center justify-center rounded-full font-mono " +
                (done
                  ? "bg-emerald-600 text-white"
                  : active
                    ? "bg-blue-600 text-white"
                    : "bg-gray-800 text-gray-500")
              }
            >
              {done ? "✓" : n}
            </span>
            <span
              className={
                done || active ? "text-gray-200" : "text-gray-500"
              }
            >
              {label}
            </span>
            {n < labels.length && (
              <span className="mx-1 h-px w-6 bg-gray-700" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
